import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Stack,
  Alert,
  Chip,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  FileDownload as FileDownloadIcon,
  VpnKey as VpnKeyIcon,
  FilterAltOutlined as FilterAltOutlinedIcon,
  RestartAlt as RestartAltIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  Videocam as VideocamIcon,
  Place as PlaceIcon,
} from '@mui/icons-material';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { themeState, authState, popupState, streamState } from '../../Recoil/RecoilState';
import ThemeSwitcher from '../../Components/Reusable/ThemeSwitcher';
import PrintBtn from '../../Components/Reusable/PrintBtn';
import DesBtn from '../../Components/Reusable/DesBtn';
import PredictionFilter from '../../Components/PopUp/Filter';
import axios from 'axios';
import { baseURL, searchFramesColumns } from '../../utils/StaticVariables';
import { useSnackbar } from 'notistack';
import ChangePassword from '../ChangePassword';

const Settings = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [themeMode] = useRecoilState(themeState);
  const { token } = useRecoilValue(authState);
  const setPopup = useSetRecoilState(popupState);
  const { enqueueSnackbar } = useSnackbar();
  const childRef = useRef(null);

  // Frame search filter & download state
  const [filter, setFilter] = useState({});
  const [filteredCount, setFilteredCount] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [camerasMap, setCamerasMap] = useState({});
  const streamAtom = useRecoilValue(streamState);

  // Fetch all user cameras to resolve IDs to names
  React.useEffect(() => {
    if (!token) return;
    axios
      .get(`${baseURL}source/user`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const list = res.data || [];
        const map = {};
        list.forEach((cam) => {
          const id = String(cam.id || cam.camera_id);
          if (id) map[id] = cam.name || id;
        });
        setCamerasMap(map);
      })
      .catch(() => {});
  }, [token]);

  // Helper to get camera names for one or more comma-separated IDs
  const getCameraNames = (cameraIds) => {
    if (!cameraIds) return "";
    const ids = Array.isArray(cameraIds) ? cameraIds : String(cameraIds).split(",");
    const streamsList = streamAtom?.data || [];
    return ids
      .map((rawId) => {
        const id = String(rawId).trim();
        if (camerasMap[id]) return camerasMap[id];
        const match = streamsList.find((s) => String(s.id || s.camera_id) === id);
        return match?.name || id;
      })
      .filter(Boolean)
      .join(", ");
  };

  // Check if any filter field is active
  const hasActiveFilter = Boolean(
    filter.startDate ||
    filter.endDate ||
    filter.startTime ||
    filter.endTime ||
    filter.camera_id ||
    filter.locations ||
    filter.areas ||
    filter.zones ||
    filter.buildings ||
    filter.floors
  );

  // Check count of records matching the filter
  const checkFilteredCount = async (appliedFilter) => {
    try {
      const params = {
        base64: false,

        ...(appliedFilter.endTime && { end_time: appliedFilter.endTime }),
        ...(appliedFilter.startTime && { start_time: appliedFilter.startTime }),
        ...(appliedFilter.startDate && { start_date: appliedFilter.startDate }),
        ...(appliedFilter.endDate && { end_date: appliedFilter.endDate }),
        ...(appliedFilter.camera_id && { camera_id: appliedFilter.camera_id }),
        ...(appliedFilter.buildings && { buildings: appliedFilter.buildings }),
        ...(appliedFilter.floors && { floors: appliedFilter.floors }),
        ...(appliedFilter.zones && { zones: appliedFilter.zones }),
        ...(appliedFilter.areas && { areas: appliedFilter.areas }),
        ...(appliedFilter.locations && { locations: appliedFilter.locations }),
      };
      const res = await axios.get(`${baseURL}workspace/search_results_with_location`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      const total = res.data?.total_count ?? res.data?.data?.length ?? 0;
      setFilteredCount(total);
      enqueueSnackbar(`Filter applied: ${total} matching records`, { variant: "info" });
    } catch (err) {
      console.error("Count check error:", err);
    }
  };

  // Open Filter Popup (uses PredictionFilter)
  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Filter Frame Search Data For Export",
      content: (
        <PredictionFilter
          ref={childRef}
          filter={filter}
          changeFilterHandle={(newFilter) => {
            setFilter(newFilter);
            checkFilteredCount(newFilter);
          }}
        />
      ),
      sendReq: () => childRef.current?.submit(),
    });
  };

  // Clear active filter
  const handleClearFilter = () => {
    setFilter({});
    setFilteredCount(null);
    enqueueSnackbar("Filter reset to all records", { variant: "info" });
  };

  // Fetch full data matching the filter and export to Excel
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const params = {
        base64: false,
        full_data: true,
        ...(filter.endTime && { end_time: filter.endTime }),
        ...(filter.startTime && { start_time: filter.startTime }),
        ...(filter.startDate && { start_date: filter.startDate }),
        ...(filter.endDate && { end_date: filter.endDate }),
        ...(filter.camera_id && { camera_id: filter.camera_id }),
        ...(filter.buildings && { buildings: filter.buildings }),
        ...(filter.floors && { floors: filter.floors }),
        ...(filter.zones && { zones: filter.zones }),
        ...(filter.areas && { areas: filter.areas }),
        ...(filter.locations && { locations: filter.locations }),
      };

      const res = await axios.get(`${baseURL}workspace/search_results_with_location`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data?.data || res.data || [];
      if (!data || data.length === 0) {
        enqueueSnackbar("No recorded frames found matching the selected filter", {
          variant: "warning",
        });
        return [];
      }

      enqueueSnackbar(`Exporting ${data.length} recorded frames to Excel...`, {
        variant: "success",
      });
      return data;
    } catch (err) {
      console.error("Error fetching frames for export:", err);
      enqueueSnackbar(
        err?.response?.data?.message || err?.response?.data?.detail || "Failed to fetch frames data for export",
        { variant: "error" }
      );
      return [];
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 1, sm: 2 } }}>
      <Tabs
        value={activeTab}
        onChange={(e, val) => setActiveTab(val)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 3,
          "& .MuiTab-root": {
            fontSize: { xs: "0.85rem", sm: "0.95rem" },
            fontWeight: 600,
            textTransform: "none",
          },
        }}
      >
        <Tab icon={<PaletteIcon />} iconPosition="start" label="General & Appearance" />
        <Tab icon={<VpnKeyIcon />} iconPosition="start" label="Change Password" />
      </Tabs>

      {activeTab === 0 && (
        <Stack spacing={3}>
          {/* Frame Search Download Section with Filter */}
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                <FileDownloadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Frame Search Data Export
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography variant="body2" color="text.secondary" mb={2}>
                Export recorded camera frames metadata and person counts directly into an Excel spreadsheet. Use the filter to customize the date, time, location, or camera scope.
              </Typography>

              {/* Action Buttons: Export & Filter */}
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                <PrintBtn
                  loading={exportLoading}
                  onExport={handleExport}
                  columns={searchFramesColumns}
                  text="Download all Data in Excel"
                />

                <DesBtn text="Filter" handle={openFilter}>
                  <FilterAltOutlinedIcon />
                </DesBtn>

                {hasActiveFilter && (
                  <Button
                    size="small"
                    color="inherit"
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    onClick={handleClearFilter}
                    sx={{
                      textTransform: "none",
                      height: 36.5,
                      marginBottom: "10px",
                    }}
                  >
                    Reset Filter
                  </Button>
                )}
              </Box>

              {/* Active Filter Indicators */}
              {hasActiveFilter && (
                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1}>
                    ACTIVE FILTERS:
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {(filter.startDate || filter.endDate) && (
                      <Chip
                        size="small"
                        icon={<CalendarIcon />}
                        label={`Date: ${filter.startDate || '...'} to ${filter.endDate || '...'}`}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {(filter.startTime || filter.endTime) && (
                      <Chip
                        size="small"
                        icon={<AccessTimeIcon />}
                        label={`Time: ${filter.startTime || '...'} to ${filter.endTime || '...'}`}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {filter.camera_id && (
                      <Chip
                        size="small"
                        icon={<VideocamIcon />}
                        label={`Camera: ${getCameraNames(filter.camera_id)}`}
                        color="secondary"
                        variant="outlined"
                      />
                    )}
                    {(filter.locations || filter.areas || filter.zones || filter.buildings) && (
                      <Chip
                        size="small"
                        icon={<PlaceIcon />}
                        label={`Location: ${[filter.locations, filter.areas, filter.zones, filter.buildings].filter(Boolean).join(' / ')}`}
                        variant="outlined"
                      />
                    )}
                  </Stack>

                  {filteredCount !== null && (
                    <Typography variant="body2" color="primary.main" fontWeight={600} mt={1}>
                      Matched Records: {filteredCount}
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Theme Settings */}
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                <PaletteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Theme & Appearance
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" gutterBottom>
                  Choose your preferred theme:
                </Typography>
                <ThemeSwitcher variant="toggle" />
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Light Mode:</strong> Standard light theme for normal viewing conditions.
                  <br />
                  <strong>Dark Mode:</strong> Dark theme to reduce eye strain in low-light environments.
                  <br />
                  <strong>High Contrast:</strong> Maximum contrast theme designed for visual accessibility.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Stack>
      )}

      {activeTab === 1 && <ChangePassword />}
    </Box>
  );
};

export default Settings;
